import {
  LegalDocLayout,
  LP, LH3, LUl, LLi, LOl, LNote, LTable,
  type LegalSection,
} from './LegalDocLayout';

const sections: LegalSection[] = [
  {
    id: 'agreement',
    heading: 'Agreement to Terms',
    body: (
      <>
        <LP>
          These Terms of Service (<strong>"Terms"</strong>) constitute a legally binding agreement
          between Zykes Technologies LLP (<strong>"Zykes"</strong>, <strong>"we"</strong>,{' '}
          <strong>"us"</strong>, or <strong>"our"</strong>) and the entity or individual (
          <strong>"Customer"</strong>, <strong>"you"</strong>, or <strong>"your"</strong>) that
          accesses or uses the GoGrabJob Business platform, APIs, and related services
          (collectively, the <strong>"Services"</strong>).
        </LP>
        <LP>
          By creating an account, clicking "I Agree", accessing the Services, or authorising your
          employees or agents to do so, you agree to be bound by these Terms and our{' '}
          <a
            href="/business/privacy-policy"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            Privacy Policy
          </a>
          , which is incorporated herein by reference.
        </LP>
        <LNote>
          If you are entering into these Terms on behalf of a company or other legal entity, you
          represent and warrant that you have authority to bind that entity. If you do not have
          such authority, you must not accept these Terms or use the Services.
        </LNote>
        <LP>
          These Terms are effective as of 1 April 2026 and supersede all prior agreements between
          you and Zykes relating to the Services.
        </LP>
      </>
    ),
  },
  {
    id: 'definitions',
    heading: 'Definitions',
    body: (
      <>
        <LP>As used in these Terms, the following terms have the meanings set out below:</LP>
        <LTable
          headers={['Term', 'Meaning']}
          rows={[
            ['"Account"', 'A registered organisational workspace within GoGrabJob Business.'],
            ['"Authorised User"', 'An employee, contractor, or agent of the Customer granted access to the Services.'],
            ['"Customer Content"', 'All data, files, and materials uploaded, submitted, or generated by the Customer within the Services.'],
            ['"Candidate Data"', 'Personal data of job applicants processed through the Services on behalf of the Customer.'],
            ['"Order Form"', 'A written or electronic agreement specifying the Services, plan tier, and commercial terms.'],
            ['"Subscription Term"', 'The period during which the Customer is licensed to use the Services, as set out in the Order Form.'],
            ['"Documentation"', 'The help guides, API references, and technical materials available at help.gograbjob.com.'],
            ['"Intellectual Property Rights"', 'Patents, copyrights, trade marks, trade secrets, and other proprietary rights.'],
            ['"DPA"', 'The Data Processing Agreement governing Zykes\'s processing of personal data on behalf of the Customer.'],
          ]}
        />
      </>
    ),
  },
  {
    id: 'eligibility',
    heading: 'Eligibility & Account Registration',
    body: (
      <>
        <LH3>3.1 Eligibility</LH3>
        <LP>
          To use GoGrabJob Business you must: (a) be at least 18 years of age; (b) be acting on
          behalf of a legitimate business entity; (c) have legal capacity to enter into binding
          contracts; and (d) not be prohibited from using the Services under applicable law.
        </LP>
        <LP>
          The Services are not available to individuals or entities that have previously had their
          accounts terminated by Zykes for violations of these Terms.
        </LP>

        <LH3>3.2 Account Registration</LH3>
        <LP>
          You must provide accurate, complete, and current information during registration.
          You are responsible for:
        </LP>
        <LUl>
          <LLi>Maintaining the confidentiality of your account credentials.</LLi>
          <LLi>All activities that occur under your account, whether or not authorised by you.</LLi>
          <LLi>
            Notifying us immediately at{' '}
            <a
              href="mailto:security@gograbjob.com"
              className="text-violet-600 hover:underline dark:text-violet-400"
            >
              security@gograbjob.com
            </a>{' '}
            of any unauthorised use of your account.
          </LLi>
          <LLi>Ensuring that all Authorised Users comply with these Terms.</LLi>
        </LUl>

        <LH3>3.3 Authorised Users</LH3>
        <LP>
          Each seat licence corresponds to one named Authorised User. Customers are responsible
          for provisioning, de-provisioning, and auditing their Authorised Users. Credential
          sharing is prohibited; each user must authenticate with their own credentials.
        </LP>
      </>
    ),
  },
  {
    id: 'subscription',
    heading: 'Subscription, Fees & Payment',
    body: (
      <>
        <LH3>4.1 Plans & Pricing</LH3>
        <LP>
          GoGrabJob Business is offered on a subscription basis across three plan tiers: Starter,
          Growth, and Enterprise. Pricing, included features, and seat limits for each plan are
          described on our{' '}
          <a
            href="/business/pricing"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            Pricing page
          </a>{' '}
          and/or the Order Form. We reserve the right to change pricing with 30 days' advance
          written notice.
        </LP>

        <LH3>4.2 Payment Terms</LH3>
        <LUl>
          <LLi>
            Subscription fees are billed in advance on a monthly or annual cycle as selected at
            checkout.
          </LLi>
          <LLi>
            Payment is due on the invoice date. Overdue amounts accrue interest at 1.5% per month
            (or the maximum permitted by law, whichever is lower).
          </LLi>
          <LLi>
            We accept credit and debit cards via our payment processor (Stripe). For Enterprise
            customers, invoice-based payment (bank transfer) is available.
          </LLi>
          <LLi>
            All fees are exclusive of applicable taxes (GST, VAT, etc.), which are your
            responsibility.
          </LLi>
        </LUl>

        <LH3>4.3 Free Trials & Starter Plan</LH3>
        <LP>
          Zykes may offer free trials or a free-tier Starter plan. During a free trial, you may
          access paid features for the trial duration without charge. At the end of the trial, your
          access reverts to the Starter tier unless you upgrade. We reserve the right to modify or
          discontinue free trials at any time.
        </LP>

        <LH3>4.4 Refund Policy</LH3>
        <LP>
          Annual subscriptions are eligible for a pro-rated refund within the first 14 days of a
          new plan term if the platform does not perform materially as described in the
          Documentation. Monthly subscriptions are non-refundable. Customised Enterprise contracts
          are governed by the terms in the applicable Order Form. Refund requests must be submitted
          to{' '}
          <a
            href="mailto:billing@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            billing@gograbjob.com
          </a>
          .
        </LP>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    heading: 'Acceptable Use Policy',
    body: (
      <>
        <LP>
          You may use the Services only for lawful business purposes in accordance with these Terms
          and all applicable laws. You agree not to:
        </LP>
        <LUl>
          <LLi>
            Use the Services to engage in unlawful discrimination in hiring based on protected
            characteristics (race, gender, age, religion, disability, etc.) under applicable
            employment law.
          </LLi>
          <LLi>
            Upload, transmit, or store content that is unlawful, defamatory, fraudulent, or
            infringes third-party Intellectual Property Rights.
          </LLi>
          <LLi>
            Reverse engineer, decompile, disassemble, or attempt to derive the source code of the
            Services.
          </LLi>
          <LLi>
            Scrape, crawl, or use automated scripts to access or extract data from the Services
            beyond what is expressly permitted by our APIs.
          </LLi>
          <LLi>
            Introduce malware, viruses, or other malicious code into the Services or connected
            systems.
          </LLi>
          <LLi>
            Circumvent or disable security features, rate limits, or access controls.
          </LLi>
          <LLi>
            Use the Services to send unsolicited commercial communications (spam) to candidates
            without their consent.
          </LLi>
          <LLi>
            Sublicence, resell, or make the Services available to third parties except as
            expressly permitted under an authorised reseller or affiliate agreement.
          </LLi>
          <LLi>
            Use the AI features of the Services to generate outputs that are used as the sole
            basis for adverse employment decisions without human review, in violation of applicable
            algorithmic decision-making laws.
          </LLi>
        </LUl>
        <LP>
          Zykes reserves the right to investigate potential violations and, without liability,
          suspend or terminate access to the Services for any Customer found to be in breach of
          this Acceptable Use Policy.
        </LP>
      </>
    ),
  },
  {
    id: 'ip',
    heading: 'Intellectual Property',
    body: (
      <>
        <LH3>6.1 Zykes Intellectual Property</LH3>
        <LP>
          Zykes and its licensors retain all right, title, and interest in and to the Services,
          including all software, algorithms, models, designs, trademarks, logos, and
          Documentation. These Terms do not grant you any ownership rights in the Services.
        </LP>
        <LP>
          Subject to your compliance with these Terms and timely payment of fees, Zykes grants
          you a limited, non-exclusive, non-transferable, revocable licence to access and use the
          Services for your internal business purposes during the Subscription Term.
        </LP>

        <LH3>6.2 Customer Content</LH3>
        <LP>
          You retain all Intellectual Property Rights in your Customer Content. By uploading or
          submitting Customer Content to the Services, you grant Zykes a limited, non-exclusive
          licence to process that content solely to provide the Services as described in these
          Terms and the DPA.
        </LP>
        <LP>
          You represent and warrant that you have all rights necessary to upload Customer Content
          and to grant the licence described above, and that Customer Content does not violate any
          applicable law or third-party rights.
        </LP>

        <LH3>6.3 Feedback</LH3>
        <LP>
          If you submit suggestions, ideas, or feedback about the Services, you grant Zykes a
          perpetual, irrevocable, royalty-free licence to use such feedback for any purpose
          without compensation to you.
        </LP>

        <LH3>6.4 AI-Generated Outputs</LH3>
        <LP>
          Outputs generated by the AI features of the Services (e.g., interview assessments,
          role-fit scores) are provided for informational purposes to support your hiring decisions.
          You remain solely responsible for all hiring decisions made using AI outputs, which must
          always be subject to human review.
        </LP>
      </>
    ),
  },
  {
    id: 'data-ownership',
    heading: 'Your Data & Data Processing',
    body: (
      <>
        <LP>
          <strong>You own your data.</strong> All Customer Content and Candidate Data you upload
          remains your property at all times. Zykes will never use your Candidate Data to train
          general-purpose AI models without your explicit written consent.
        </LP>
        <LP>
          The processing of personal data through the Services is governed by our Data Processing
          Agreement (DPA), which forms part of these Terms. The DPA reflects the requirements of
          the GDPR, the UK GDPR, and the India DPDPA 2023. You may download the DPA from your
          account settings or request it from{' '}
          <a
            href="mailto:legal@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            legal@gograbjob.com
          </a>
          .
        </LP>
        <LP>
          Upon termination of your subscription, we will make your Customer Content available for
          export for 90 days. After that window, we will securely delete it in accordance with
          our data retention policies unless you request earlier deletion or a longer retention
          period applies by law.
        </LP>
      </>
    ),
  },
  {
    id: 'third-party',
    heading: 'Third-Party Integrations & APIs',
    body: (
      <>
        <LP>
          The Services may integrate with or connect to third-party services and platforms (e.g.,
          LinkedIn, Gmail, Workday, Naukri). Your use of those third-party services is subject to
          their respective terms and privacy policies, which you agree to independently. Zykes is
          not responsible for the functionality, availability, or security of third-party services.
        </LP>
        <LP>
          Access to our REST and GraphQL APIs is subject to the API Terms embedded in the
          Documentation. API keys are account credentials; you are responsible for their security.
          We reserve the right to throttle or revoke API access for abuse or policy violation.
        </LP>
      </>
    ),
  },
  {
    id: 'warranties',
    heading: 'Warranties & Disclaimers',
    body: (
      <>
        <LH3>9.1 Zykes Warranties</LH3>
        <LP>Zykes warrants that:</LP>
        <LUl>
          <LLi>
            The Services will perform materially in accordance with the Documentation during the
            Subscription Term.
          </LLi>
          <LLi>
            We will implement commercially reasonable security measures as described in our
            Security Overview.
          </LLi>
          <LLi>
            We will not intentionally include malicious code in the Services.
          </LLi>
        </LUl>

        <LH3>9.2 Disclaimers</LH3>
        <LP>
          <strong>
            EXCEPT AS EXPRESSLY SET OUT IN SECTION 9.1, THE SERVICES ARE PROVIDED "AS IS" AND
            "AS AVAILABLE". TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ZYKES DISCLAIMS
            ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND UNINTERRUPTED OR ERROR-FREE
            OPERATION.
          </strong>
        </LP>
        <LP>
          We do not warrant that the Services will meet your specific hiring outcomes, that AI
          assessments will be free from bias or error, or that any candidate data processed
          through the Services is accurate or complete (as such data is provided by you or third
          parties).
        </LP>
      </>
    ),
  },
  {
    id: 'liability',
    heading: 'Limitation of Liability',
    body: (
      <>
        <LH3>10.1 Indirect Damages</LH3>
        <LP>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NEITHER PARTY WILL BE LIABLE FOR
          ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR EXEMPLARY DAMAGES (INCLUDING LOST
          PROFITS, LOST DATA, OR LOSS OF BUSINESS) ARISING OUT OF OR RELATED TO THESE TERMS,
          EVEN IF THAT PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </LP>

        <LH3>10.2 Aggregate Liability Cap</LH3>
        <LP>
          ZYKES'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS WILL NOT
          EXCEED THE FEES PAID BY YOU IN THE 12 MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING
          RISE TO THE CLAIM.
        </LP>

        <LH3>10.3 Exceptions</LH3>
        <LP>
          Nothing in these Terms limits or excludes liability for: (a) death or personal injury
          caused by negligence; (b) fraud or fraudulent misrepresentation; (c) any liability that
          cannot be excluded or limited under applicable law; or (d) a party's obligation to pay
          amounts validly due under these Terms.
        </LP>
        <LNote>
          For Enterprise customers, bespoke liability caps and indemnification terms may be
          negotiated in the applicable Order Form.
        </LNote>
      </>
    ),
  },
  {
    id: 'indemnification',
    heading: 'Indemnification',
    body: (
      <>
        <LP>
          You agree to defend, indemnify, and hold harmless Zykes and its officers, directors,
          employees, and agents from and against any claims, liabilities, damages, costs, and
          expenses (including reasonable legal fees) arising out of or relating to:
        </LP>
        <LUl>
          <LLi>Your breach of these Terms or the Acceptable Use Policy.</LLi>
          <LLi>
            Your use of the Services in a manner that violates applicable employment, privacy, or
            anti-discrimination law.
          </LLi>
          <LLi>
            Any Customer Content that infringes a third party's Intellectual Property Rights or
            contains unlawful material.
          </LLi>
          <LLi>
            Your failure to obtain and maintain valid consent from candidates whose personal data
            is processed through the Services.
          </LLi>
        </LUl>
        <LP>
          Zykes will similarly indemnify you against third-party claims that the Services, as
          provided by Zykes and used in accordance with these Terms, infringe any registered
          patent, copyright, or trade mark. Zykes's indemnification obligations do not apply to
          claims arising from your modifications to the Services or combinations with third-party
          products not authorised by Zykes.
        </LP>
      </>
    ),
  },
  {
    id: 'termination',
    heading: 'Term & Termination',
    body: (
      <>
        <LH3>12.1 Term</LH3>
        <LP>
          These Terms commence on the date you first access the Services and continue until your
          Subscription Term expires or is terminated in accordance with this section.
        </LP>

        <LH3>12.2 Termination by You</LH3>
        <LP>
          You may cancel your subscription at any time through your account settings. Cancellation
          takes effect at the end of the current billing period. No pro-rated refunds are provided
          for unused portions of a monthly subscription; annual subscriptions follow the refund
          policy in Section 4.4.
        </LP>

        <LH3>12.3 Termination by Zykes</LH3>
        <LP>
          Zykes may suspend or terminate your access to the Services with immediate effect if:
        </LP>
        <LUl>
          <LLi>You materially breach these Terms and fail to remedy the breach within 14 days of notice.</LLi>
          <LLi>You fail to pay fees when due and do not remedy such failure within 7 days of notice.</LLi>
          <LLi>You become insolvent, enter administration, or are subject to bankruptcy proceedings.</LLi>
          <LLi>Continued operation would expose Zykes to legal liability or harm other customers.</LLi>
        </LUl>
        <LP>
          Zykes may also terminate the Services on 60 days' written notice for any other business
          reason, in which case a pro-rated refund of prepaid fees will be provided.
        </LP>

        <LH3>12.4 Effect of Termination</LH3>
        <LP>
          Upon termination: (a) all licences granted hereunder immediately cease; (b) you may
          export your Customer Content within the 90-day post-termination window; and (c) Sections
          6, 9, 10, 11, 12, 13, and 14 survive termination.
        </LP>
      </>
    ),
  },
  {
    id: 'governing-law',
    heading: 'Governing Law & Dispute Resolution',
    body: (
      <>
        <LH3>13.1 Governing Law</LH3>
        <LP>
          These Terms are governed by and construed in accordance with the laws of India, without
          regard to its conflict of law provisions.
        </LP>

        <LH3>13.2 Dispute Resolution</LH3>
        <LP>
          In the event of any dispute, claim, or controversy arising out of or relating to these
          Terms or the Services, the parties will first attempt to resolve the matter through good
          faith negotiation for a period of 30 days.
        </LP>
        <LP>
          If the dispute cannot be resolved through negotiation, it shall be submitted to binding
          arbitration under the Arbitration and Conciliation Act, 1996 of India, conducted in
          English in Bengaluru, Karnataka, before a sole arbitrator agreed upon by the parties (or
          appointed by the relevant authority if agreement cannot be reached within 15 days).
        </LP>
        <LP>
          Either party may seek emergency injunctive relief from a court of competent jurisdiction
          to preserve the status quo pending arbitration. For Enterprise customers in the EEA or
          UK, jurisdiction clauses in the applicable Order Form will take precedence.
        </LP>
      </>
    ),
  },
  {
    id: 'general',
    heading: 'General Provisions',
    body: (
      <>
        <LH3>14.1 Entire Agreement</LH3>
        <LP>
          These Terms, together with the Order Form (if any), Privacy Policy, Cookie Policy, DPA,
          and any other policies expressly incorporated by reference, constitute the entire
          agreement between you and Zykes relating to the Services and supersede all prior
          negotiations, representations, or agreements.
        </LP>

        <LH3>14.2 Amendments</LH3>
        <LP>
          Zykes may modify these Terms with 30 days' advance written notice for material changes.
          Continued use of the Services after the effective date constitutes acceptance. Changes
          required by law or to address security vulnerabilities may take effect immediately.
        </LP>

        <LH3>14.3 Waiver & Severability</LH3>
        <LP>
          Failure by either party to enforce any provision of these Terms does not constitute a
          waiver of future enforcement. If any provision is found invalid or unenforceable, the
          remaining provisions will continue in full force and effect.
        </LP>

        <LH3>14.4 Assignment</LH3>
        <LP>
          You may not assign these Terms or transfer your account without Zykes's prior written
          consent. Zykes may assign these Terms in connection with a merger, acquisition, or sale
          of substantially all of its assets, with notice to you.
        </LP>

        <LH3>14.5 Force Majeure</LH3>
        <LP>
          Neither party will be liable for delay or failure to perform obligations caused by
          events outside their reasonable control, including natural disasters, war, acts of
          government, or internet infrastructure failures, provided that such party gives prompt
          written notice and uses reasonable efforts to resume performance.
        </LP>

        <LH3>14.6 Notices</LH3>
        <LP>
          Legal notices to Zykes must be sent to{' '}
          <a
            href="mailto:legal@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            legal@gograbjob.com
          </a>
          . Notices to you will be sent to the email address on your account. Notices sent by
          email are deemed received on the next business day following transmission.
        </LP>
      </>
    ),
  },
];

export function TermsOfServicePage() {
  return (
    <LegalDocLayout
      category="Legal"
      title="Terms of Service"
      subtitle="The agreement governing your use of GoGrabJob Business — plain English where possible, with nothing hidden."
      effectiveDate="1 April 2026"
      lastUpdated="14 April 2026"
      version="3.0"
      sections={sections}
      contactEmail="legal@gograbjob.com"
    />
  );
}
